import React, { useEffect } from 'react';
import { bool, func, node, number, string } from 'prop-types';
import {
  Box,
  Button,
  capitalize,
  Divider,
  Drawer,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

// utils
import { useUserRolePermissions } from 'hooks';

// Styles
import { useLocation } from 'react-router-dom';

// Components
import MobileMenu from '../supplier-authorizations/cards-panel/header/MobileActivationToggleMenu.connector';

const MobileDrawer = ({
  activationClick,
  cardsLength,
  hasWizardAccess,
  filtersClick,
  filterIdentifierCount,
  isSequencingWizardEnabled,
  mobileDrawerOpen,
  newAuthorizationLabel,
  panel,
  setMobileDrawerOpen,
  tooltip,
  type,
}) => {
  const { pathname } = useLocation();
  const { isProductLoadingManage } = useUserRolePermissions();

  const onSupplierAuth = pathname.includes('supplier-authorizations');
  const onAuthSequencing = pathname.includes('authorization-sequencing');
  const showWizardButton = isProductLoadingManage && isSequencingWizardEnabled;
  const newLabel = onSupplierAuth
    ? 'Add New'
    : onAuthSequencing
    ? 'Launch Wizard'
    : newAuthorizationLabel;

  const disableFilterButton = cardsLength ? cardsLength < 2 : null;

  useEffect(() => {
    // ensure mobile drawer control is false when unmounted
    return () => setMobileDrawerOpen(false);
  }, [setMobileDrawerOpen]);

  return (
    <Drawer
      anchor="right"
      onClose={() => setMobileDrawerOpen(false)}
      open={mobileDrawerOpen}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'surface.light',
            // removes 1 px of white space on left hand side
            left: -1,
            top: 56,
            width: '100%',
          },
        },
      }}
      variant="persistent"
    >
      {isProductLoadingManage && (
        <Toolbar
          disableGutters
          sx={{
            backgroundColor: 'transparent',
            boxShadow: 1,
            color: 'secondary.main',
            display: 'grid',
            gridTemplateColumns: onSupplierAuth
              ? 'repeat(3, 1fr)'
              : showWizardButton
              ? 'repeat(2, 1fr)'
              : '1fr',
            minHeight: 56,
            width: '100%',
            zIndex: 4,
          }}
        >
          {showWizardButton && (
            <Box sx={{ display: 'flex', height: '100%' }}>
              <Tooltip
                disableTouchListener={hasWizardAccess}
                enterTouchDelay={10}
                placement="top"
                title={tooltip}
              >
                <Box sx={{ width: 1 }}>
                  <MenuItem
                    disabled={!hasWizardAccess}
                    onClick={activationClick}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'secondary.main',
                      },
                      backgroundColor: !hasWizardAccess ? 'graphic.light' : 'secondary.main',
                      color: !hasWizardAccess ? 'text.disabled' : 'surface.white',
                      height: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    <AddRoundedIcon />
                    {newLabel}
                  </MenuItem>
                </Box>
              </Tooltip>
            </Box>
          )}
          {onSupplierAuth && (
            <Box sx={{ display: 'flex', height: '100%' }}>
              <MenuItem
                sx={{
                  // This divider style needs to stay otherwise the root of MenuItem
                  // adds margin to the divider component
                  '&+.MuiDivider-root': {
                    marginBottom: 0,
                    marginTop: 0,
                  },
                  '&:hover': {
                    backgroundColor: 'surface.white',
                  },
                  backgroundColor: 'surface.white',
                  color: 'secondary.main',
                  height: '100%',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <MobileMenu viewBy={type} />
              </MenuItem>
              <Divider
                orientation="vertical"
                sx={{
                  height: '100%',
                  width: '1px',
                  zIndex: 10,
                }}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', height: '100%' }}>
            <MenuItem
              disabled={disableFilterButton}
              onClick={filtersClick}
              sx={{
                '&:hover': {
                  backgroundColor: 'surface.white',
                },
                backgroundColor: 'surface.white',
                color: 'secondary.main',
                height: '100%',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              Filters
              {filterIdentifierCount >= 1 && (
                <Typography
                  sx={{
                    alignItems: 'center',
                    backgroundColor: 'primary.light',
                    borderRadius: 2,
                    color: 'primary.main',
                    display: 'flex',
                    fontSize: 12,
                    height: 16,
                    justifyContent: 'center',
                    ml: 1,
                    p: 2,
                    width: 14,
                  }}
                >
                  {filterIdentifierCount}
                </Typography>
              )}
              <ChevronRightRoundedIcon />
            </MenuItem>
          </Box>
        </Toolbar>
      )}
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        margin="0 auto"
        maxWidth={948}
        pb={1}
        pt={2}
        px={3}
        width={1}
      >
        <Button
          onClick={() => setMobileDrawerOpen(false)}
          sx={theme => ({
            ...theme.typography.button,
            justifyContent: 'flex-start',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            mb: { xs: 0, sm: 0.5 },
            ml: -1,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            mr: { xs: 0, sm: 3 },
            mt: 0,
            pl: 0,
          })}
          variant="text"
        >
          <ChevronLeftRoundedIcon sx={{ mt: '-2px' }} />
          {capitalize(type)}
        </Button>
        {!isProductLoadingManage && (
          <Button disabled={disableFilterButton} onClick={filtersClick} variant="outlined">
            Filters
            {filterIdentifierCount >= 1 && (
              <Typography
                sx={{
                  alignItems: 'center',
                  backgroundColor: 'primary.light',
                  borderRadius: 2,
                  color: 'primary.main',
                  display: 'flex',
                  fontSize: 12,
                  height: 16,
                  justifyContent: 'center',
                  ml: 1,
                  p: 2,
                  width: 14,
                }}
              >
                {filterIdentifierCount}
              </Typography>
            )}
            <ChevronRightRoundedIcon
              sx={{
                ml: filterIdentifierCount ? 0.5 : 1,
                mr: -1,
                my: 0,
              }}
            />
          </Button>
        )}
      </Box>
      <Box bgcolor="surface.light" height="100%" margin="0 auto" px={1} width={1}>
        {panel}
      </Box>
    </Drawer>
  );
};

MobileDrawer.propTypes = {
  activationClick: func.isRequired,
  cardsLength: number,
  filterIdentifierCount: number.isRequired,
  filtersClick: func.isRequired,
  hasWizardAccess: bool,
  isSequencingWizardEnabled: bool,
  mobileDrawerOpen: bool.isRequired,
  newAuthorizationLabel: string,
  panel: node.isRequired,
  setMobileDrawerOpen: func.isRequired,
  tooltip: string,
  type: string,
};

MobileDrawer.defaultProps = {
  cardsLength: null,
  hasWizardAccess: true,
  isSequencingWizardEnabled: true,
  newAuthorizationLabel: 'New Authorizations',
  tooltip: '',
  type: '',
};

export default MobileDrawer;
